// Opening a show from the directory, which is the write this screen could not
// make until the service accepted a session for it.
//
// ---------------------------------------------------------------------------
// WHY THIS IS HERE AND NOT ON SETTINGS
//
// Settings is where a nova_admin strengthens a sign-in. Your activations is
// where they look at the estate. Opening a row is a fact about the estate, so
// the button lives on the list it will appear in. Putting it on Settings would
// hide the one write that produces a row behind the one write that does not.
//
// ---------------------------------------------------------------------------
// TWO REQUESTS, TWO CREDENTIALS, AND THEY ARE NOT INTERCHANGEABLE
//
// Creating a client is `requireAdminSession`: the person is already signed in,
// and the row records their identity. Opening an activation is `requireOpener`:
// a current authenticator code on *this* request, the same confirmation kiosk
// publish already uses. A session that proved a factor at login is not enough
// to add a show, because adding a show is not reading a chart.
//
// The six digits therefore travel only on the second request. Creating a client
// does not spend them, so a failed open after a successful create can be retried
// with the next code without producing a second company.
//
// ---------------------------------------------------------------------------
// NOTHING HERE IS A PROJECT
//
// XP1 has a client and an activation. The show's name, venue, timezone and run
// *are* the activation. A third object invented so the form has a step named
// "project" would be a row nothing in the schema can hold, and a name the
// directory would then have to pretend it understood.
//
// Hours stay off this form. They are set on Show details against the
// activation's own clock, and a window typed here would be a second editor
// for a record that page already owns.
//
// A viewer grant stays off it too. Opening a row and granting sight of it are
// different decisions, and bundling them is how a show appears on a client's
// list because somebody was in a hurry to see it themselves.
//
// ---------------------------------------------------------------------------
// THE DIRECTORY IS REFRESHED AND THE DASHBOARD IS NOT OPENED
//
// After a 200 the list is asked for again, so the new row is the server's. This
// file does not navigate into the show. Opening the dashboard is a report read
// that records its own access row, and a create that also filed one would be a
// prefetch wearing a convenience's clothes.

import { useEffect, useState, type ReactNode } from "react";
import { NoticeBox, RowPick } from "@nova/desk-ui/parts";
import { FieldRow, Info } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import {
  createActivation,
  createClient,
  createIdentity,
  listClientChoices,
  recordAgreement,
  type AdminRefusal,
  type ClientChoice,
} from "../api/admin.js";
import type { ApiOptions } from "../api/client.js";
import {
  AddPersonFields,
  emptyPersonDraft,
  identityWriteFromDraft,
  type PersonDraft,
} from "../people/AddPersonFields.js";
import { ShowTemplatePicker } from "../registration/ShowTemplatePicker.js";
import { storeShowTemplate, type ShowTemplateId } from "../registration/showTemplates.js";
import { AdminRefused, Authorise } from "./Authorise.js";
import { TimeZoneField } from "./TimeZoneField.js";
import type { SessionToken } from "../session/store.js";

export type OpenShowProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly onSessionEnded: (detail: string) => void;
  /** The directory is stale. The owner re-reads it. This file does not open the show. */
  readonly onOpened: () => void;
  /**
   * Whether "New client" is offered (roleAccess.ts, `addClient`): creating a
   * company, its first contact and its agreement are an Admin's. An Editor
   * adds a show to a client they hold, and the picker lists only those.
   */
  readonly newClient: boolean;
};

type Clients =
  | { readonly status: "idle" }
  | { readonly status: "reading" }
  | { readonly status: "read"; readonly clients: readonly ClientChoice[] }
  | { readonly status: "failed"; readonly refusal: AdminRefusal };

type Who = "existing" | "new";

export function OpenShow({ api, token, onSessionEnded, onOpened, newClient }: OpenShowProps): ReactNode {
  const [open, setOpen] = useState(false);
  const [listed, setListed] = useState<Clients>({ status: "idle" });
  const [who, setWho] = useState<Who>("existing");
  const [clientId, setClientId] = useState("");
  const [clientName, setClientName] = useState("");
  const [name, setName] = useState("");
  const [timeZone, setTimeZone] = useState("");
  const [venue, setVenue] = useState("");
  const [opensOn, setOpensOn] = useState("");
  const [closesOn, setClosesOn] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<AdminRefusal | null>(null);
  const [runNote, setRunNote] = useState<string | null>(null);
  const [opened, setOpened] = useState<string | null>(null);
  const [personDraft, setPersonDraft] = useState<PersonDraft>(emptyPersonDraft);
  const [signedOn, setSignedOn] = useState("");
  const [retentionMonths, setRetentionMonths] = useState("");
  const [noticesPerson, setNoticesPerson] = useState("");
  const [noticesEmail, setNoticesEmail] = useState("");
  const [noticesPersonDirty, setNoticesPersonDirty] = useState(false);
  const [noticesEmailDirty, setNoticesEmailDirty] = useState(false);
  const [template, setTemplate] = useState<ShowTemplateId | null>(null);
  const [madeClientId, setMadeClientId] = useState<string | null>(null);
  const [madeIdentity, setMadeIdentity] = useState(false);
  const [madeAgreement, setMadeAgreement] = useState(false);

  useEffect(() => {
    if (noticesPersonDirty) return;
    const fromPerson = `${personDraft.firstName} ${personDraft.lastName}`.trim();
    setNoticesPerson(fromPerson || clientName.trim());
  }, [personDraft.firstName, personDraft.lastName, clientName, noticesPersonDirty]);

  useEffect(() => {
    if (noticesEmailDirty) return;
    setNoticesEmail(personDraft.email.trim());
  }, [personDraft.email, noticesEmailDirty]);

  const begin = async (): Promise<void> => {
    setOpen(true);
    setRefusal(null);
    setListed({ status: "reading" });

    // The clients this reader can add a show to, as the service says: every
    // live one for an Admin, the ones they hold for an Editor.
    const result = await listClientChoices(api, token);
    if (!result.ok) {
      setListed({ status: "failed", refusal: result });
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      return;
    }

    const clients = result.value.clients;
    setListed({ status: "read", clients });
    setMadeClientId(null);
    setMadeIdentity(false);
    setMadeAgreement(false);
    if (clients.length === 0) {
      setWho(newClient ? "new" : "existing");
      return;
    }
    setWho("existing");
    if (clientId === "" || !clients.some((client) => client.clientId === clientId)) {
      setClientId(clients[0]!.clientId);
    }
  };

  const submit = async (clients: readonly ClientChoice[]): Promise<void> => {
    const first = opensOn.trim();
    const last = closesOn.trim();
    if ((first === "") !== (last === "")) {
      setRefusal(null);
      setRunNote("Enter both the first and last day.");
      return;
    }
    if (first !== "" && last !== "" && last < first) {
      setRefusal(null);
      setRunNote("The last day must be on or after the first day.");
      return;
    }

    setBusy(true);
    setRefusal(null);
    setRunNote(null);

    let target = clientId;
    let known = clients;

    if (who === "new") {
      const contactName = `${personDraft.firstName} ${personDraft.lastName}`.trim();
      if (clientName.trim() === "") {
        setBusy(false);
        setRunNote("Enter the client's name.");
        return;
      }
      if (personDraft.firstName.trim() === "" || personDraft.lastName.trim() === "" || personDraft.email.trim() === "") {
        setBusy(false);
        setRunNote("Enter the first contact's name and email.");
        return;
      }
      if (signedOn.trim() === "" || !Number.isFinite(Number(retentionMonths.trim())) || Number(retentionMonths.trim()) < 1) {
        setBusy(false);
        setRunNote("Record the processing agreement: a signed-on date and retention in months.");
        return;
      }

      if (madeClientId === null) {
        const made = await createClient(api, token, clientName);
        if (!made.ok) {
          setBusy(false);
          setRefusal(made);
          if (made.kind === "session_ended") onSessionEnded(made.detail);
          return;
        }
        target = made.value.clientId;
        setMadeClientId(made.value.clientId);
        // The picker's two facts about it, a name and an id, which is all the
        // choices list carries for any client.
        known = [...clients, { clientId: made.value.clientId, name: made.value.name }];
        setListed({ status: "read", clients: known });
        setClientId(made.value.clientId);
      } else {
        target = madeClientId;
      }

      if (!madeIdentity) {
        const person = await createIdentity(api, token, identityWriteFromDraft(personDraft, target));
        if (!person.ok) {
          setBusy(false);
          setRefusal(person);
          if (person.kind === "session_ended") onSessionEnded(person.detail);
          return;
        }
        setMadeIdentity(true);
      }

      if (!madeAgreement) {
        const agreement = await recordAgreement(api, token, target, {
          signedOn: signedOn.trim(),
          retentionMonths: Number(retentionMonths.trim()),
          noticesPerson:
            (noticesPerson.trim() === "" ? contactName || clientName.trim() : noticesPerson.trim()) ||
            null,
          noticesEmail: (noticesEmail.trim() === "" ? personDraft.email.trim() : noticesEmail.trim()) || null,
        });
        if (!agreement.ok) {
          setBusy(false);
          setRefusal(agreement);
          if (agreement.kind === "session_ended") onSessionEnded(agreement.detail);
          return;
        }
        setMadeAgreement(true);
      }
    }

    const activationId = mintActivationId();
    const result = await createActivation(api, token, {
      activationId,
      clientId: target,
      name,
      timeZone,
      secondFactorCode: code,
      ...(venue.trim() === "" ? {} : { venue: venue.trim() }),
      ...(first === "" || last === "" ? {} : { opensOn: first, closesOn: last }),
    });

    // Cleared on both outcomes, per Authorise.tsx: the step is spent either way.
    // createClient above did not send these digits and must not have cleared them.
    setCode("");
    setBusy(false);

    if (result.ok) {
      if (template !== null) storeShowTemplate(result.value.activationId, template);
      setOpened(result.value.name);
      setOpen(false);
      onOpened();
      return;
    }

    setRefusal(result);
    if (result.kind === "session_ended") onSessionEnded(result.detail);
  };

  if (!open) {
    return (
      <div className="console-open-show">
        {opened === null ? null : (
          <NoticeBox kind="good" title={`${opened} added`}>
            <p>Set opening hours on Show details, then set up screens under Kiosk.</p>
          </NoticeBox>
        )}
        {/* Flush under the page's head, as the design draws it; spaced only when
            the notice of the show just added sits above it. */}
        <div className={opened === null ? "acts acts-flush" : "acts"}>
          <button type="button" className="btn" onClick={() => void begin()}>
            Add activation
          </button>
        </div>
      </div>
    );
  }

  // THE DESIGN'S PANEL (TASKS §143, step 6): the heading inside it, the client
  // as two rows to pick with the choice of client inside the same set, and
  // the show's own four facts side by side. Kept from live: the time zone (the
  // dashboard's ranges are the venue's days), the starting template, the
  // authenticator and the refusals. `console-open-show` is the checks' handle.
  if (listed.status === "reading") {
    return (
      <section className="panel panel-stack console-open-show">
        <h2 className="sec" style={{ margin: 0 }}>
          Add activation
        </h2>
        <Loading rows={2} cols={1} label="Loading clients…" />
      </section>
    );
  }

  if (listed.status === "failed") {
    return (
      <section className="panel panel-stack console-open-show">
        <h2 className="sec" style={{ margin: 0 }}>
          Add activation
        </h2>
        <AdminRefused refusal={listed.refusal} />
        <div className="acts acts-flush">
          <button type="button" className="btn" onClick={() => void begin()}>
            Try again
          </button>
          <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)}>
            Not now
          </button>
        </div>
      </section>
    );
  }

  if (listed.status !== "read") return null;

  const clients = listed.clients;

  return (
    <section className="panel panel-stack console-open-show">
      {/* The October hand-off's (i)s: the heading's and each fieldset's
          sentence behind the mark beside its words, rather than a line of help
          under them. A legend is not a label, so the (i) can sit in it without
          taking a control's name. */}
      <h2 className="sec" style={{ margin: 0 }}>
        Add activation
        <Info label="About Add activation">Add the show now. Set hours, screens and access afterwards.</Info>
      </h2>

      <fieldset className="set">
        <legend className="label">Client</legend>
        {/* No choice of existing or new for a reader who cannot add a company
            (an Editor): the select alone, of the clients they hold. */}
        {newClient ? (
          <div className="rows">
            <RowPick
              type="radio"
              name="open-show-who"
              value="existing"
              title="Existing client"
              on={who === "existing"}
              onPick={() => setWho("existing")}
              disabled={busy || clients.length === 0}
            />
            <RowPick
              type="radio"
              name="open-show-who"
              value="new"
              title="New client"
              on={who === "new"}
              onPick={() => setWho("new")}
              disabled={busy}
            />
          </div>
        ) : null}
        {!newClient && clients.length === 0 ? (
          <p className="help help-tight">
            You hold no live client to add a show to. An Admin gives an Editor their clients on Team.
          </p>
        ) : who === "existing" ? (
          <FieldRow label="Which client">
            <select
              className="input"
              value={clientId}
              onChange={(event) => setClientId(event.target.value)}
              disabled={busy || clients.length === 0}
            >
              {clients.length === 0 ? <option value="">No live client has been recorded yet</option> : null}
              {clients.map((client) => (
                <option key={client.clientId} value={client.clientId}>
                  {client.name}
                </option>
              ))}
            </select>
          </FieldRow>
        ) : (
          <FieldRow label="The client’s name">
            <input
              className="input"
              value={clientName}
              onChange={(event) => setClientName(event.target.value)}
              disabled={busy}
            />
          </FieldRow>
        )}
      </fieldset>

      {who === "new" && newClient ? (
        <>
          <fieldset className="set">
            <legend className="label">
              First contact
              <Info label="About First contact">
                Names the person who can approve registration setup. Adding them does not grant Console
                access.
              </Info>
            </legend>
            <AddPersonFields draft={personDraft} onDraft={setPersonDraft} busy={busy} />
          </fieldset>

          <fieldset className="set">
            <legend className="label">
              Processing agreement
              <Info label="About Processing agreement">
                The signed agreement that allows personal-data exports for this client.
              </Info>
            </legend>
            <div className="pair-wide">
              <FieldRow label="Signed on" help="The date on the document.">
                <input
                  className="input"
                  type="date"
                  value={signedOn}
                  onChange={(event) => setSignedOn(event.target.value)}
                  disabled={busy}
                />
              </FieldRow>
              <FieldRow
                label="Retention period, in months"
                help="What the agreement says is kept. An agreement has to name one."
              >
                <input
                  className="input"
                  inputMode="numeric"
                  value={retentionMonths}
                  onChange={(event) => setRetentionMonths(event.target.value)}
                  disabled={busy}
                />
              </FieldRow>
              <FieldRow label="Named contact" help="Left unchanged, this is the first contact's name.">
                <input
                  className="input"
                  value={noticesPerson}
                  onChange={(event) => {
                    setNoticesPersonDirty(true);
                    setNoticesPerson(event.target.value);
                  }}
                  disabled={busy}
                />
              </FieldRow>
              <FieldRow
                label="Address for notices"
                help="Left unchanged, this is the first contact's email. This does not need to be a Console user."
              >
                <input
                  className="input"
                  type="email"
                  value={noticesEmail}
                  onChange={(event) => {
                    setNoticesEmailDirty(true);
                    setNoticesEmail(event.target.value);
                  }}
                  disabled={busy}
                />
              </FieldRow>
            </div>
          </fieldset>
        </>
      ) : null}

      <div className="pair-wide">
        <FieldRow label="What the show is called">
          <input className="input" value={name} onChange={(event) => setName(event.target.value)} disabled={busy} />
        </FieldRow>
        <FieldRow label="Where it is held" help="Optional. Add it later if the venue is not confirmed.">
          <input className="input" value={venue} onChange={(event) => setVenue(event.target.value)} disabled={busy} />
        </FieldRow>
        <FieldRow label="The first day">
          <input
            className="input"
            type="date"
            value={opensOn}
            onChange={(event) => {
              setOpensOn(event.target.value);
              setRunNote(null);
            }}
            disabled={busy}
          />
        </FieldRow>
        <FieldRow label="The last day" help="Optional. Enter both dates. Set opening hours on Show details.">
          <input
            className="input"
            type="date"
            value={closesOn}
            onChange={(event) => {
              setClosesOn(event.target.value);
              setRunNote(null);
            }}
            disabled={busy}
          />
        </FieldRow>
      </div>
      {runNote === null ? null : (
        <p className="help help-tight console-run-note" role="alert">
          {runNote}
        </p>
      )}

      <TimeZoneField value={timeZone} onChange={setTimeZone} disabled={busy} />

      <ShowTemplatePicker value={template} onPick={setTemplate} disabled={busy} />

      <Authorise
        value={code}
        onChange={setCode}
        busy={busy}
        what="create this activation"
        onEnter={() => {
          if (!busy) void submit(clients);
        }}
      />

      <div className="acts acts-flush">
        <button
          type="button"
          className="btn"
          disabled={busy || (!newClient && clients.length === 0)}
          onClick={() => void submit(clients)}
        >
          {busy ? "Creating…" : "Create this activation"}
        </button>
        <button type="button" className="btn btn-quiet" disabled={busy} onClick={() => setOpen(false)}>
          Not now
        </button>
      </div>

      {refusal === null ? null : <AdminRefused refusal={refusal} />}
    </section>
  );
}

/**
 * The activation id the caller has to choose, minted here because a person
 * should not have to paste a uuid to open a show.
 *
 * crypto.randomUUID, and nothing homemade. The service takes a uuid it did not
 * generate so a retry with the same body is a conflict rather than a second
 * row; inventing a lookalike in this file would be a second opinion about what
 * a uuid is.
 */
function mintActivationId(): string {
  if (typeof crypto.randomUUID !== "function") {
    throw new Error("this browser cannot mint an activation id");
  }
  return crypto.randomUUID();
}
